import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import LanguageSwitcher from '../LanguageSwitcher';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const { t, i18n } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle body scroll lock
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const getLocalizedPath = (path: string) => {
    return i18n.language === 'en' ? path : `/${i18n.language}${path}`;
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-md py-1 md:py-4 z-50' 
          : 'bg-transparent py-1 md:py-4 z-30'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between px-6 md:px-0">
          {/* Logo */}
          <Link 
            to={getLocalizedPath('/')}
            className="text-primary font-bold text-2xl -ml-8 md:ml-0"
            onClick={(e) => {
              if (window.location.pathname === '/') {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            <img 
              src="/logo1.svg" 
              alt="Company Logo" 
              className="h-16 md:h-28 w-auto -mt-2"
            />
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link 
              to={getLocalizedPath('/#process')}
              className="text-text/80 hover:text-primary transition-colors duration-200"
              onClick={(e) => {
                if (window.location.pathname === getLocalizedPath('/')) {
                  e.preventDefault();
                  document.getElementById('process')?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              {t('header.nav.howItWorks')}
            </Link>
            <Link 
              to={getLocalizedPath('/#features')}
              className="text-text/80 hover:text-primary transition-colors duration-200"
              onClick={(e) => {
                if (window.location.pathname === getLocalizedPath('/')) {
                  e.preventDefault();
                  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
            >
              {t('header.nav.features')}
            </Link>
            <Link 
              to={getLocalizedPath('/about')}
              onClick={() => {
                if (window.location.pathname === getLocalizedPath('/about')) {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }}
              className="text-text/80 hover:text-primary transition-colors duration-200"
            >
              {t('header.nav.about')}
            </Link>
            <Link 
              to={getLocalizedPath('/about#contact')}
              className="text-text/80 hover:text-primary transition-colors duration-200"
            >
              {t('header.nav.contact')}
            </Link>
            <Link 
              to={getLocalizedPath('/faq')}
              className="text-text/80 hover:text-primary transition-colors duration-200"
            >
              {t('header.nav.faq')}
            </Link>
            <LanguageSwitcher />
          </nav>

          {/* CTA Button */}
          <a 
            href={getLocalizedPath('/#quote-request')}
            onClick={(e) => {
              if (window.location.pathname === getLocalizedPath('/')) {
                e.preventDefault();
                document.getElementById('quote-request')?.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-primary text-white px-4 md:px-6 py-2 md:py-2.5 rounded-lg 
              hover:bg-primary-light transition-all duration-200 text-sm md:text-base
              shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            {t('header.cta')}
          </a>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden p-2 z-50 relative rounded-lg transition-all duration-200 ${
              isMobileMenuOpen 
                ? 'text-white' 
                : isScrolled 
                  ? 'text-primary bg-white/95 backdrop-blur-md' 
                  : 'text-primary'
            }`}
            aria-label="Toggle menu"
          >
            <svg 
              className="w-6 h-6 transition-colors duration-200"
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d={isMobileMenuOpen 
                  ? "M6 18L18 6M6 6l12 12" 
                  : "M4 6h16M4 12h16M4 18h16"} 
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-primary z-40 md:hidden pt-20"
            style={{ height: '100vh' }}
          >
            <div className="flex flex-col items-center px-4">
              <nav className="flex flex-col items-center space-y-6">
                <Link 
                  to={getLocalizedPath('/#process')}
                  onClick={(e) => {
                    if (window.location.pathname === getLocalizedPath('/')) {
                      e.preventDefault();
                      document.getElementById('process')?.scrollIntoView({ behavior: 'smooth' });
                    }
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-white/90 hover:text-white text-lg font-medium"
                >
                  {t('header.nav.howItWorks')}
                </Link>
                <Link 
                  to={getLocalizedPath('/#features')}
                  onClick={(e) => {
                    if (window.location.pathname === getLocalizedPath('/')) {
                      e.preventDefault();
                      document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                    }
                    setIsMobileMenuOpen(false);
                  }}
                  className="text-white/90 hover:text-white text-lg font-medium"
                >
                  {t('header.nav.features')}
                </Link>
                <Link 
                  to={getLocalizedPath('/about')}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    if (window.location.pathname === getLocalizedPath('/about')) {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                  className="text-white/90 hover:text-white text-lg font-medium"
                >
                  {t('header.nav.about')}
                </Link>
                <Link 
                  to={getLocalizedPath('/about#contact')}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-white/90 hover:text-white text-lg font-medium"
                >
                  {t('header.nav.contact')}
                </Link>
                <Link 
                  to={getLocalizedPath('/faq')}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-white/90 hover:text-white text-lg font-medium"
                >
                  {t('header.nav.faq')}
                </Link>
                <LanguageSwitcher />
              </nav>
              
              <a 
                href={getLocalizedPath('/#quote-request')}
                onClick={(e) => {
                  if (window.location.pathname === getLocalizedPath('/')) {
                    e.preventDefault();
                    document.getElementById('quote-request')?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="mt-8 bg-white text-primary px-8 py-3 rounded-lg 
                  font-medium text-lg shadow-lg hover:bg-white/90 
                  transition-all duration-200"
              >
                {t('header.cta')}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}